import { useState } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { Send, Check, AlertCircle, User, Phone, Globe, Clock, Mail, MessageSquare } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ContactForm = () => {
  const { t, language } = useLanguage();
  const [formData, setFormData] = useState({
    full_name: '',
    country: '',
    phone: '',
    email: '',
    best_contact_time: '',
    message: '',
  });
  const [status, setStatus] = useState('idle'); // idle, loading, success, error
  const [errors, setErrors] = useState({});

  const validateForm = () => {
    const newErrors = {};
    if (!formData.full_name.trim()) {
      newErrors.full_name = true;
    }
    if (!formData.country.trim()) {
      newErrors.country = true;
    }
    if (!formData.phone.trim()) {
      newErrors.phone = true;
    }
    if (!formData.best_contact_time) {
      newErrors.best_contact_time = true;
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;

    setStatus('loading');

    try {
      await axios.post(`${API}/leads`, formData);
      setStatus('success');
      setFormData({
        full_name: '',
        country: '',
        phone: '',
        email: '',
        best_contact_time: '',
        message: '',
      });
    } catch (error) {
      console.error('Error submitting form:', error);
      setStatus('error');
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: false }));
    }
  };

  const contactTimes = [
    { value: 'morning', label: t('form_morning') },
    { value: 'noon', label: t('form_noon') },
    { value: 'evening', label: t('form_evening') },
  ];

  return (
    <section 
      id="contact"
      className="py-20 md:py-28"
      style={{ backgroundColor: 'var(--warm-white)' }}
      data-testid="contact-section"
    >
      <div className="max-w-4xl mx-auto px-4 md:px-6">
        {/* Section Header */}
        <div className="text-center mb-10 md:mb-12">
          <h2 
            className="text-3xl md:text-4xl mb-4"
            style={{ fontFamily: 'Cormorant Garamond, serif', color: 'var(--charcoal)' }}
          >
            {t('contact_title')}
          </h2>
          <p className="text-gray-600 text-lg">
            {t('contact_subtitle')}
          </p>
        </div>

        {/* Form Card */}
        <div 
          className="card-organic p-6 md:p-10"
          data-testid="contact-form-card"
        >
          {status === 'success' ? (
            <div 
              className="text-center py-12"
              data-testid="form-success-message"
            >
              <div 
                className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-6"
                style={{ backgroundColor: 'var(--sage-100)' }}
              >
                <Check size={32} style={{ color: 'var(--sage-600)' }} />
              </div>
              <h3 
                className="text-2xl mb-2"
                style={{ fontFamily: 'Cormorant Garamond, serif', color: 'var(--charcoal)' }}
              >
                {t('form_success')}
              </h3>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Name & Country Row */}
              <div className="grid md:grid-cols-2 gap-4 md:gap-6">
                <div>
                  <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-2">
                    <User size={16} />
                    {t('form_name')} *
                  </label>
                  <input
                    type="text"
                    name="full_name"
                    value={formData.full_name}
                    onChange={handleChange}
                    className={`w-full px-4 py-3 rounded-xl border transition-colors focus:outline-none focus:ring-2 ${
                      errors.full_name 
                        ? 'border-red-300 focus:ring-red-200' 
                        : 'border-gray-200 focus:ring-sage-200 focus:border-sage-400'
                    }`}
                    style={{ backgroundColor: 'var(--cream)' }}
                    data-testid="form-name-input"
                  />
                </div>

                <div>
                  <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-2">
                    <Globe size={16} />
                    {t('form_country')} *
                  </label>
                  <input
                    type="text"
                    name="country"
                    value={formData.country}
                    onChange={handleChange}
                    className={`w-full px-4 py-3 rounded-xl border transition-colors focus:outline-none focus:ring-2 ${
                      errors.country 
                        ? 'border-red-300 focus:ring-red-200' 
                        : 'border-gray-200 focus:ring-sage-200 focus:border-sage-400'
                    }`}
                    style={{ backgroundColor: 'var(--cream)' }}
                    data-testid="form-country-input"
                  />
                </div>
              </div>

              {/* Phone & Email Row */}
              <div className="grid md:grid-cols-2 gap-4 md:gap-6">
                <div>
                  <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-2">
                    <Phone size={16} />
                    {t('form_phone')} *
                  </label>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    className={`w-full px-4 py-3 rounded-xl border transition-colors focus:outline-none focus:ring-2 ${
                      errors.phone 
                        ? 'border-red-300 focus:ring-red-200' 
                        : 'border-gray-200 focus:ring-sage-200 focus:border-sage-400'
                    }`}
                    style={{ backgroundColor: 'var(--cream)' }}
                    dir="ltr"
                    data-testid="form-phone-input"
                  />
                </div>

                <div>
                  <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-2">
                    <Mail size={16} />
                    {t('form_email')}
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    className="w-full px-4 py-3 rounded-xl border border-gray-200 transition-colors focus:outline-none focus:ring-2 focus:ring-sage-200 focus:border-sage-400"
                    style={{ backgroundColor: 'var(--cream)' }}
                    dir="ltr"
                    data-testid="form-email-input"
                  />
                </div>
              </div>

              {/* Best Contact Time */}
              <div>
                <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-3">
                  <Clock size={16} />
                  {t('form_best_time')} *
                </label>
                <div className="flex flex-wrap gap-3">
                  {contactTimes.map((time) => (
                    <label 
                      key={time.value}
                      className={`flex items-center gap-2 px-5 py-3 rounded-full cursor-pointer transition-all ${
                        formData.best_contact_time === time.value
                          ? 'bg-sage-600 text-white shadow-md'
                          : 'bg-gray-100 hover:bg-gray-200'
                      } ${errors.best_contact_time && !formData.best_contact_time ? 'ring-2 ring-red-200' : ''}`}
                      style={formData.best_contact_time === time.value ? { backgroundColor: 'var(--sage-600)' } : {}}
                    >
                      <input
                        type="radio"
                        name="best_contact_time"
                        value={time.value}
                        checked={formData.best_contact_time === time.value}
                        onChange={handleChange}
                        className="sr-only"
                        data-testid={`form-time-${time.value}`}
                      />
                      {time.label}
                    </label>
                  ))}
                </div>
              </div>

              {/* Message */}
              <div>
                <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-2">
                  <MessageSquare size={16} />
                  {t('form_message')}
                </label>
                <textarea
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  rows={4}
                  className="w-full px-4 py-3 rounded-xl border border-gray-200 transition-colors focus:outline-none focus:ring-2 focus:ring-sage-200 focus:border-sage-400 resize-none"
                  style={{ backgroundColor: 'var(--cream)' }}
                  data-testid="form-message-input"
                />
              </div>

              {/* Privacy Consent */}
              <div className="flex items-start gap-3">
                <input
                  type="checkbox"
                  id="privacyConsent"
                  name="privacyConsent"
                  checked={formData.privacyConsent || false}
                  onChange={handleChange}
                  className="mt-1 w-4 h-4 rounded border-gray-300"
                  style={{ accentColor: 'var(--sage-600)' }}
                  required
                  data-testid="form-privacy-consent"
                />
                <label htmlFor="privacyConsent" className="text-sm text-gray-600 leading-relaxed">
                  {language === 'he' 
                    ? 'אני מאשר/ת שקראתי והסכמתי למדיניות הפרטיות. המידע שמסרתי ישמש ליצירת קשר חוזר בלבד ולא יועבר לצדדים שלישיים.'
                    : 'I confirm that I have read and agree to the Privacy Policy. The information I provided will be used solely for follow-up contact and will not be shared with third parties.'
                  }
                </label>
              </div>

              {/* Error Message */}
              {status === 'error' && (
                <div 
                  className="flex items-center gap-2 p-4 rounded-xl bg-red-50 text-red-700"
                  data-testid="form-error-message"
                >
                  <AlertCircle size={20} />
                  {t('form_error')}
                </div>
              )}

              {/* Submit Button */}
              <button
                type="submit"
                disabled={status === 'loading'}
                className="w-full btn-primary flex items-center justify-center gap-2 py-4"
                data-testid="form-submit-btn"
              >
                {status === 'loading' ? (
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                ) : (
                  <>
                    <Send size={18} className={language === 'he' ? 'rotate-180' : ''} />
                    {t('form_submit')}
                  </>
                )}
              </button>
            </form>
          )}
        </div>
      </div>
    </section>
  );
};

export default ContactForm;
